// src/components/WorkflowReassignModal.js
import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import MultiUserSelect from "./MultiUserSelect";
import { useApi } from "../hooks/useApi";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * A workflow step's assignees, with the legacy fallback for responses that predate the
 * assignee list: `{ ids, names }` from `assignedToUserIds` / `assignedToNames`, else the single
 * `assignedToUserId` / `assignedToName`. Used for display, the Complete check and the modal's
 * initial value.
 */
export const stepAssignees = (step) => ({
    ids: step?.assignedToUserIds ?? (step?.assignedToUserId != null ? [step.assignedToUserId] : []),
    names: step?.assignedToNames ?? (step?.assignedToName ? [step.assignedToName] : []),
});

/**
 * "Change assignees" dialog for a workflow step. Same structure as the app's assign dialog
 * (AssignRiskModal / RemediationUpdateModal): fixed overlay, header with a close X, a labelled
 * MultiUserSelect (up to 10 users, pre-filled with `initialUserIds`), an optional note, an error
 * box, Cancel / Confirm. Confirm stays disabled while the list is empty.
 *
 * Assignable users (`GET /assessments/assignableUsers`) load when the modal opens. `onConfirm({
 * userIds, comment })` replaces the step's assignee set and must reject on failure: the error is
 * shown inside the modal and it stays open; on success the caller closes it. Both buttons are
 * disabled while the request is in flight.
 */
const WorkflowReassignModal = ({ isOpen, stepName, hasAssignee = true, initialUserIds = [], onConfirm, onClose }) => {
    const title = hasAssignee ? "Change assignees" : "Assign";
    const verb = hasAssignee ? "Reassign" : "Assign";
    const apiCall = useApi();
    // The api hook is re-created per render; keep it in a ref so the load effect depends on isOpen only.
    const apiRef = useRef(apiCall);
    useEffect(() => { apiRef.current = apiCall; });
    // Read on open only, so a parent re-render does not reset the user's edits.
    const initialRef = useRef(initialUserIds);
    useEffect(() => { initialRef.current = initialUserIds; });

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userIds, setUserIds] = useState([]);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        let cancelled = false;
        setUserIds((initialRef.current || []).map(String));
        setComment("");
        setError(null);
        setUsersLoading(true);
        (async () => {
            try {
                const res = await apiRef.current("GET", "/assessments/assignableUsers");
                if (!cancelled) setUsers(Array.isArray(res) ? res : []);
            } catch {
                if (!cancelled) setUsers([]);
            } finally {
                if (!cancelled) setUsersLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (userIds.length === 0) {
            setError("Select at least one assignee.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm({ userIds: userIds.map((id) => parseInt(id, 10)), comment: comment.trim() || null });
        } catch (err) {
            setError(errorMessage(err, "The step could not be reassigned."));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-overlay overflow-y-auto h-full w-full z-50" data-testid="workflow-reassign-modal">
            <div className="relative top-20 mx-auto p-5 border border-border w-[500px] max-w-[95vw] shadow-lg rounded-md bg-theme-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label="Close"
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {stepName && (
                        <p className="text-sm text-content-secondary">
                            Step: <span className="font-medium text-content-primary">{stepName}</span>
                        </p>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-2">
                            Assignees (any one can complete the step)
                        </label>
                        <MultiUserSelect
                            value={userIds}
                            onChange={setUserIds}
                            users={users}
                            loading={usersLoading}
                            disabled={submitting}
                            placeholder="-- Add a user --"
                        />
                    </div>

                    <div>
                        <label htmlFor="wf-reassign-comment" className="block text-sm font-medium text-content-secondary mb-2">
                            Note for the assignees (optional)
                        </label>
                        <input
                            id="wf-reassign-comment"
                            type="text"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            disabled={submitting}
                            className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan disabled:opacity-50"
                        />
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-4 py-2 border border-border rounded-md text-sm font-medium text-content-primary hover:bg-theme-primary disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || usersLoading || userIds.length === 0}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? `${verb}ing...` : `Confirm ${verb.toLowerCase()}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WorkflowReassignModal;
